import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'

import { LeadDto } from '@model/LeadDto'

import { Loading } from '@shared/components/Loading/Loading'

import { ReferralTokenValues, getReferralTokenValues } from './ReferralToken'
import {
  ReferralParams,
  getReferralParams,
  getReferralParamsOrCookieForRedirect,
  getRegistrationReferralParams,
  isAffiliatesParams,
  isLandingPage,
  isReferralDefault,
  isReferralLandingPage,
  isReferralLink,
  isSGLLink,
  setAffiliatesParamsOrCookie,
  setLinkClickedCookie,
  setReferralCode,
  setReferralParams,
  setReferralParamsOrCookie,
  setSGLParamsOrCookie,
} from './helpers'

import styles from './Referral.module.scss'

interface UseReferral {
  getReferralTokenValues: (lead?: LeadDto) => ReferralTokenValues
  isAffiliatesParams: () => boolean
}

export const useReferral = (): UseReferral => {
  const location = useLocation()

  const search = location.search
  const referralParams = getRegistrationReferralParams(search)

  return {
    getReferralTokenValues: (lead?: LeadDto) => getReferralTokenValues(referralParams, lead),
    isAffiliatesParams: () => isAffiliatesParams(referralParams),
  }
}

export const Referral: React.FunctionComponent<PropsWithChildren<{}>> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const clientApiClient = useApiClient(ClientApiClient)
  const { callGAEvents } = useGAEvents()
  const [isRedirecting, setIsRedirecting] = useState(false)

  const waitForGA = useCallback(async (): Promise<void> => {
    if (ReactGA.isInitialized) return

    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        resolve()
      }, 5000)

      const checkInitialized = () => {
        if (ReactGA.isInitialized) {
          clearTimeout(timeout)
          resolve()
        } else {
          setTimeout(checkInitialized, 100)
        }
      }

      checkInitialized()
    })
  }, [])

  const cookieHandler = async () => {
    const params = getReferralParams(location.search)
    if (isLandingPage(params)) {
      await setCookies(params)

      if (params.landingPageUrl) {
        setIsRedirecting(true)

        await waitForGA()

        callGAEvents({
          landing_page_url: params.landingPageUrl,
          transport: 'beacon',
        }).ReferralMaterials.PromoAssets.landingPageRedirection()

        setTimeout(() => {
          if (isReferralLandingPage(params)) {
            window.location.href = getRedirectUrl(params)
          } else {
            setIsRedirecting(false)
            navigate('/404')
          }
        }, 300)
      }
    } else {
      await setCookies(params)
    }
  }

  useEffect(() => {
    cookieHandler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setCookies = async (params: ReferralParams) => {
    if (isReferralLink(params)) {
      await clientApiClient.validateIntroducingBrokerByCode(params.utm_content).then(() => {
        setReferralParams(params)
        setReferralCode(params)
        setLinkClickedCookie()
      })
    } else if (isAffiliatesParams(params)) {
      setAffiliatesParamsOrCookie(params)
    } else if (isSGLLink(params)) {
      setSGLParamsOrCookie(params)
    } else if (isReferralDefault(params)) {
      setReferralParamsOrCookie(params)
    }
  }

  const getRedirectUrl = (params: ReferralParams): string => {
    const url = new URL(decodeURIComponent(params.landingPageUrl))
    const urlSearchParams = new URLSearchParams(url.search)

    const urlParams = Object.entries(Object.fromEntries(urlSearchParams.entries()))
      .filter(([key, value]) => !!key && !!value)
      .reduce((previousValue, [key, value]) => {
        return {
          ...previousValue,
          [key]: value,
        }
      }, {})

    const referralParams = getReferralParamsOrCookieForRedirect(location.search)

    return `${url.origin}${url.pathname}?${new URLSearchParams({
      ...urlParams,
      ...referralParams,
    })}`
  }

  if (isRedirecting) {
    return (
      <div className={styles.redirectionView}>
        <Loading isLoading={isRedirecting} showLoadingIcon />
      </div>
    )
  }

  return <>{children}</>
}
